import { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import type { InventoryUnit, WishlistItem } from '@/types';

const DAY = 86400000;

const MOCK_INVENTORY: InventoryUnit[] = [
  { id: 'inv-1', dealership_id: 'demo-dealership', vin: '1FTFW1RG5NFA12345', stock_number: 'P2401',
    make: 'Toyota', model: 'Tacoma TRD', year: 2022, color: 'White', price: 41500,
    status: 'available', eta_date: null, notes: null,
    created_at: new Date(Date.now() - 5*DAY).toISOString(), updated_at: new Date(Date.now() - 5*DAY).toISOString() },
  { id: 'inv-2', dealership_id: 'demo-dealership', vin: null, stock_number: 'P2402',
    make: 'Toyota', model: 'Tacoma TRD Off-Road', year: 2023, color: 'Silver', price: 43200,
    status: 'incoming', eta_date: new Date(Date.now() + 6*DAY).toISOString().slice(0,10), notes: null,
    created_at: new Date(Date.now() - 1*DAY).toISOString(), updated_at: new Date(Date.now() - 1*DAY).toISOString() },
  { id: 'inv-3', dealership_id: 'demo-dealership', vin: null, stock_number: 'P2403',
    make: 'Ford', model: 'Bronco Wildtrak', year: 2023, color: 'Black', price: 56800,
    status: 'available', eta_date: null, notes: null,
    created_at: new Date(Date.now() - 12*DAY).toISOString(), updated_at: new Date(Date.now() - 12*DAY).toISOString() },
  { id: 'inv-4', dealership_id: 'demo-dealership', vin: null, stock_number: 'P2404',
    make: 'Chevrolet', model: 'Tahoe LT', year: 2022, color: 'Black', price: 60400,
    status: 'incoming', eta_date: new Date(Date.now() + 14*DAY).toISOString().slice(0,10), notes: null,
    created_at: new Date(Date.now() - 2*DAY).toISOString(), updated_at: new Date(Date.now() - 2*DAY).toISOString() },
  { id: 'inv-5', dealership_id: 'demo-dealership', vin: null, stock_number: 'P2405',
    make: 'Hyundai', model: 'Palisade Calligraphy', year: 2022, color: 'Grey', price: 47200,
    status: 'available', eta_date: null, notes: null,
    created_at: new Date(Date.now() - 8*DAY).toISOString(), updated_at: new Date(Date.now() - 8*DAY).toISOString() },
];

export function useInventory() {
  const { mode, dealershipId } = useAuth();
  const [units, setUnits] = useState<InventoryUnit[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (mode === 'demo') { setUnits(MOCK_INVENTORY); setLoading(false); return; }
    if (mode !== 'real' || !dealershipId) { setUnits([]); setLoading(false); return; }
    setLoading(true);
    const { data } = await supabase
      .from('inventory_units')
      .select('*')
      .eq('dealership_id', dealershipId)
      .neq('status', 'sold')
      .order('created_at', { ascending: false });
    setUnits((data ?? []) as InventoryUnit[]);
    setLoading(false);
  }, [mode, dealershipId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (mode !== 'real' || !dealershipId) return;
    const ch = supabase.channel(`inv:${dealershipId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'inventory_units', filter: `dealership_id=eq.${dealershipId}` }, () => load())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [mode, dealershipId, load]);

  return { units, loading, refresh: load };
}

/** Match a wishlist item to inventory units based on make/model/year/price. */
export function matchInventory(w: WishlistItem, units: InventoryUnit[]): InventoryUnit[] {
  const wMake = w.make.trim().toLowerCase();
  const wModel = (w.model ?? '').trim().toLowerCase();
  return units.filter(u => {
    if (u.status === 'sold') return false;
    if (u.make.trim().toLowerCase() !== wMake) return false;
    if (wModel) {
      const uModel = (u.model ?? '').trim().toLowerCase();
      if (!uModel.includes(wModel.split(' ')[0])) return false;
    }
    if (w.year_min && u.year && u.year < w.year_min) return false;
    if (w.year_max && u.year && u.year > w.year_max) return false;
    if (w.max_price && u.price && Number(u.price) > Number(w.max_price)) return false;
    return true;
  });
}

export function useMatchesFor(w: WishlistItem) {
  const { units } = useInventory();
  return useMemo(() => matchInventory(w, units), [w, units]);
}
